import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Clock, Star, FileCheck } from 'lucide-react';
import { getLocalDateTimeString } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminApprovals() {
  const { getAuthHeaders } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/submissions/pending`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setSubmissions(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleApprove = async (submissionId) => {
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/submissions/${submissionId}/approve`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ approved: true })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Approved! ${data.points_awarded} points awarded!`);
        fetchSubmissions();
      } else {
        toast.error('Failed to approve submission');
      }
    } catch (error) {
      toast.error('Failed to approve submission');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedSubmission) return;
    
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/submissions/${selectedSubmission.submission_id}/approve`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          approved: false, 
          rejection_reason: rejectionReason || 'Task not completed correctly'
        })
      });

      if (response.ok) {
        toast.success('Submission rejected');
        setSelectedSubmission(null);
        setRejectionReason('');
        fetchSubmissions();
      } else {
        toast.error('Failed to reject submission');
      }
    } catch (error) {
      toast.error('Failed to reject submission');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#08513F] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-[#264653] font-outfit">Task Approvals</h1>
          <p className="text-[#6B7280] mt-1">
            {submissions.length} pending {submissions.length === 1 ? 'submission' : 'submissions'}
          </p>
        </div>

        {/* Submissions List */}
        {submissions.length === 0 ? (
          <Card className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg p-12 text-center">
            <FileCheck className="w-16 h-16 text-green-500/50 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#264653] font-outfit mb-2">All Caught Up!</h2>
            <p className="text-[#6B7280]">No pending submissions to review.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {submissions.map((submission, index) => (
              <Card 
                key={submission.submission_id}
                className="bg-white rounded-2xl border-l-4 border-l-yellow-400 border-[#D4AF37]/20 shadow-md hover:shadow-lg transition-shadow animate-slide-up"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`submission-card-${submission.submission_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-14 w-14 border-2 border-[#D4AF37]/30">
                        <AvatarImage src={submission.child?.profile_image} />
                        <AvatarFallback className={`font-bold ${
                          submission.child?.gender === 'Muslim' 
                            ? 'bg-[#08513F] text-white' 
                            : 'bg-[#E76F51] text-white'
                        }`}>
                          {submission.child?.first_name?.charAt(0)}{submission.child?.last_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-[#264653] font-outfit">
                            {submission.child?.first_name} {submission.child?.last_name}
                          </h3>
                          <Badge className="bg-yellow-100 text-yellow-800 rounded-full">
                            <Clock className="w-3 h-3 mr-1" />
                            Pending
                          </Badge>
                        </div>
                        
                        <p className="text-lg font-semibold text-[#08513F] mb-1">
                          {submission.task?.name}
                        </p>
                        <p className="text-sm text-[#6B7280] mb-2">
                          {submission.task?.description}
                        </p>
                        
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1 text-[#D4AF37] font-semibold">
                            <Star className="w-4 h-4" />
                            {submission.task?.points} pts
                          </span>
                          <span className="text-[#6B7280]">
                            {getLocalDateTimeString(submission.submission_date)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Button
                        onClick={() => handleApprove(submission.submission_id)}
                        disabled={processing}
                        className="bg-green-500 hover:bg-green-600 text-white rounded-xl px-6"
                        data-testid={`approve-${submission.submission_id}`}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => setSelectedSubmission(submission)}
                        disabled={processing}
                        variant="outline"
                        className="border-red-200 text-red-500 hover:bg-red-50 rounded-xl px-6"
                        data-testid={`reject-${submission.submission_id}`}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Rejection Dialog */}
        <Dialog open={!!selectedSubmission} onOpenChange={(open) => {
          if (!open) {
            setSelectedSubmission(null);
            setRejectionReason('');
          }
        }}>
          <DialogContent className="sm:max-w-md rounded-3xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-[#264653] font-outfit">
                Reject Submission
              </DialogTitle>
              <DialogDescription>
                Please provide a reason for rejection (optional)
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g., Task was not fully completed"
                className="rounded-xl"
                data-testid="rejection-reason-input"
              />
              
              <div className="flex gap-3">
                <Button
                  onClick={() => setSelectedSubmission(null)}
                  variant="outline"
                  className="flex-1 rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReject}
                  disabled={processing}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl"
                  data-testid="confirm-reject-btn"
                >
                  {processing ? 'Rejecting...' : 'Confirm Reject'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
